import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import {
  assert_equal,
  assert_false,
  assert_key_in_record,
  assert_number_gte_0,
  assert_string_not_empty,
  assert_type_enum,
} from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { CONSTANTS_GENERATION__value_range } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { ENUM__image_size_name, ENUM_UTILS__image_size_name } from '@shared/constants/ENUM__image_size_name';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { ENUM__image_orientation } from '../_enums/ENUM__image_orientation';
import { ENUM__image_size_aspect_ratio } from '../_enums/ENUM__image_size_aspect_ratio';

export class DTO__image_size extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.IMAGE_SIZE;

  static readonly ZERO = new DTO__image_size(0, 0);
  static readonly SQUARE_512 = new DTO__image_size(512, 512);

  /*
   * STATIC METHODS
   */
  static sort(a: DTO__image_size, b: DTO__image_size) {
    return a.pixels_amount - b.pixels_amount;
  }

  static from_string(value: string): DTO__image_size {
    let [width, height] = value.split('x');

    let width_number = assert_number_gte_0(parseInt(width), { context: 'width' });
    let height_number = assert_number_gte_0(parseInt(height), { context: 'height' });

    return new DTO__image_size(width_number, height_number);
  }

  static create(aspect_ratio: string, multiplier: number): DTO__image_size {
    let out_map = new Map<string, DTO__image_size>([
      //horizontal
      [ENUM__image_size_aspect_ratio.w3h1, new DTO__image_size(111, 37)],
      [ENUM__image_size_aspect_ratio.w2h1, new DTO__image_size(91, 45.5)],
      [ENUM__image_size_aspect_ratio.w16h9, new DTO__image_size(80, 45)],
      [ENUM__image_size_aspect_ratio.w3h2, new DTO__image_size(75, 50)],
      [ENUM__image_size_aspect_ratio.w4h3, new DTO__image_size(64, 48)],
      [ENUM__image_size_aspect_ratio.w5h4, new DTO__image_size(62.5, 50)],
      [ENUM__image_size_aspect_ratio.w6h5, new DTO__image_size(75, 62.5)],

      //square
      [ENUM__image_size_aspect_ratio.w1h1, new DTO__image_size(64, 64)],

      //vertical
      [ENUM__image_size_aspect_ratio.w5h6, new DTO__image_size(62.5, 75)],
      [ENUM__image_size_aspect_ratio.w4h5, new DTO__image_size(50, 62.5)],
      [ENUM__image_size_aspect_ratio.w3h4, new DTO__image_size(48, 64)],
      [ENUM__image_size_aspect_ratio.w2h3, new DTO__image_size(50, 75)],
      [ENUM__image_size_aspect_ratio.w9h16, new DTO__image_size(45, 80)],
      [ENUM__image_size_aspect_ratio.w1h2, new DTO__image_size(45.5, 91)],
      [ENUM__image_size_aspect_ratio.w1h3, new DTO__image_size(37, 111)],
    ]);

    let out = out_map.get_or_assert(aspect_ratio);

    out = new DTO__image_size(out.width * multiplier, out.height * multiplier);

    return out;
  }

  static scale_to_container(size_image: DTO__image_size, size_container: DTO__image_size) {
    if (size_image.width == 0) return DTO__image_size.ZERO.clone();
    if (size_image.height == 0) return DTO__image_size.ZERO.clone();
    if (size_container.width == 0) return DTO__image_size.ZERO.clone();
    if (size_container.height == 0) return DTO__image_size.ZERO.clone();

    const scale = Math.min(size_container.width / size_image.width, size_container.height / size_image.height);

    let out = new DTO__image_size(size_image.width * scale, size_image.height * scale);

    return out;
  }

  static get_longest_side_for(image_size_name: ENUM__image_size_name, aspect_ratio: number): number {
    const image_size_square = ENUM_UTILS__image_size_name.get_image_side_square(image_size_name);

    assert_number_gte_0(aspect_ratio);

    // Normalize aspect ratio so ratio_factor is always >= 1 (e.g., 0.5 -> 2)
    const ratio_factor = aspect_ratio >= 1 ? aspect_ratio : 1 / aspect_ratio;

    // 1. Calculate exact continuous width for target area
    let exact_short_side = image_size_square / Math.sqrt(ratio_factor);
    exact_short_side = exact_short_side.snap_to_8();

    // 3. Derive long side so both sides remain valid integers / multiples of 8
    let out = exact_short_side * ratio_factor;

    out = out.snap_to_8_and_clamp(CONSTANTS_GENERATION__value_range.IMAGE_SIZE_OUT.min, CONSTANTS_GENERATION__value_range.IMAGE_SIZE_OUT.max);

    return out;
  }

  /*
   * FIELDS
   */
  width: number;
  height: number;

  aspect_ratio: number;
  aspect_ratio_x: number;
  aspect_ratio_y: number;
  aspect_ratio_string: string;

  orientation: ENUM__image_orientation;
  pixels_amount: number;

  /*
   * METHODS
   */

  constructor(width: number = CONSTANTS__shared.NUMBER_EMPTY_VALUE, height: number = CONSTANTS__shared.NUMBER_EMPTY_VALUE) {
    super();

    [this.width, this.height] = [width, height];

    const gcd = this.getGreatestCommonDivisor(width, height);

    this.pixels_amount = width * height;

    this.aspect_ratio = width / height;
    [this.aspect_ratio_x, this.aspect_ratio_y] = [width / gcd, height / gcd];

    this.aspect_ratio_string = `${this.aspect_ratio_x}:${this.aspect_ratio_y}`;
    this.aspect_ratio = this.aspect_ratio.to_fixed(2);

    [this.aspect_ratio_x, this.aspect_ratio_y] = [this.aspect_ratio_x.to_fixed(2), this.aspect_ratio_y.to_fixed(2)];

    if (width == height) {
      this.orientation = ENUM__image_orientation.SQUARE;
    } else if (width > height) {
      this.orientation = ENUM__image_orientation.HORIZONTAL;
    } else {
      this.orientation = ENUM__image_orientation.VERTICAL;
    }
  }

  getGreatestCommonDivisor(a: number, b: number): number {
    if (!b) return a;

    return this.getGreatestCommonDivisor(b, a % b);
  }

  toString(): string {
    return `${this.width}x${this.height}`;
  }

  scale(width_max: number): DTO__image_size {
    let [width_new, height_new] = [this.width, this.height];

    if (this.width > this.height) {
      width_new = width_max;
      height_new = Math.round((this.height * width_max) / this.width);
    } else {
      height_new = width_max;
      width_new = Math.round((this.width * width_max) / this.height);
    }

    return new DTO__image_size(width_new, height_new);
  }

  is_equal(other: DTO__image_size): boolean {
    assert_key_in_record(other, CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE);
    assert_equal(other[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE], DTO__image_size._class_name_static, {
      assert_type: ENUM__error_type.VALIDATION_INVALID_TYPE,
    });

    return this.width == other.width && this.height == other.height;
  }

  is_vertical(): boolean {
    return this.height > this.width;
  }

  is_horizontal(): boolean {
    return this.width > this.height;
  }

  is_square(): boolean {
    return this.width === this.height;
  }

  is_zero(): boolean {
    return this.width == 0 && this.height == 0;
  }

  get_side_max(): number {
    return Math.max(this.width, this.height);
  }

  class_name_fit_view(): string {
    assert_false(this.is_stub());

    return this.is_horizontal() ? 'w-full h-fit' : 'h-full w-fit';
  }

  to_css_object() {
    return { width: `${this.width}px`, height: `${this.height}px` };
  }

  multiply_of(multiple_of: number) {
    let [width, height] = [this.width, this.height];

    width = width - (width % multiple_of);
    height = height - (height % multiple_of);

    return new DTO__image_size(width, height);
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {
    assert_number_gte_0(this.width);
    assert_number_gte_0(this.height);

    assert_number_gte_0(this.aspect_ratio);
    assert_number_gte_0(this.aspect_ratio_x);
    assert_number_gte_0(this.aspect_ratio_y);
    assert_string_not_empty(this.aspect_ratio_string);

    assert_type_enum(this.orientation, ENUM__image_orientation);
    assert_number_gte_0(this.pixels_amount);
  }
}
